import type { AuthProvidersResponse, MeResponse } from '@agent-platform/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getRouteApi, useRouter } from '@tanstack/react-router';
import { Activity, Bot, KeyRound, Workflow } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ErrorAlert } from '@/components/error-alert';
import { Logo3D } from '@/components/logo-3d';
import Ferrofluid from '@/components/reactbits/Ferrofluid';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { safeRedirect } from '@/lib/format';
import { useApplyMe } from '@/lib/queries';

const route = getRouteApi('/login');

/** The Google "G" mark (brand colours; lucide has no brand icons). */
function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"
      />
    </svg>
  );
}

/** What the product does, shown beside the form on wide screens. */
const FEATURES = [
  { icon: Bot, text: 'Agents with tools, MCP servers and skills' },
  { icon: Workflow, text: 'Workflows that run on demand or on a schedule' },
  { icon: KeyRound, text: 'Every agent served as an authenticated endpoint' },
  { icon: Activity, text: 'Traces, metrics and usage for every run' },
];

/** Frosted fields for the glass card (autofill would otherwise paint them light blue). */
const glassInput =
  'h-10 border-white/10 bg-white/[0.06] px-4 text-white placeholder:text-white/40 focus-visible:border-white/40 focus-visible:ring-white/15 autofill:shadow-[inset_0_0_0_1000px_rgb(22_22_26)] autofill:[-webkit-text-fill-color:white]';

export function LoginPage() {
  const { redirect, error } = route.useSearch();
  const router = useRouter();
  const applyMe = useApplyMe();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const providers = useQuery({
    queryKey: ['auth-providers'],
    queryFn: () => api<AuthProvidersResponse>('/auth/providers'),
    staleTime: 5 * 60_000,
  });
  const googleUrl = `/api/admin/auth/google/start${
    redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''
  }`;

  const login = useMutation({
    mutationFn: () => api<MeResponse>('/auth/login', { method: 'POST', body: { email, password } }),
    onSuccess: (me) => {
      applyMe(me);
      router.history.push(safeRedirect(redirect));
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate();
  };

  return (
    <div className="relative min-h-svh overflow-hidden bg-[#0b0b0f]">
      {/* React Bits Ferrofluid (WebGL2); without WebGL2 it renders nothing and the dark backdrop shows. */}
      <div className="absolute inset-0" data-testid="login-background">
        <Ferrofluid
          colors={['#ffffff', '#ffffff', '#ffffff']}
          spikeHeight={1}
          sharpness={2.5}
          tilt={48}
          mouseInteraction
          clickPulse
        />
      </div>
      {/* Keeps the copy and the form readable over the brightest spikes: a column-wide shade on
          wide screens, a centred one when the page stacks. */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgb(11_11_15/0.55),transparent_70%)] lg:bg-[linear-gradient(90deg,rgb(11_11_15/0.85)_0%,rgb(11_11_15/0.6)_45%,transparent_75%)]"
        aria-hidden
      />
      <div className="relative z-10 mx-auto grid min-h-svh w-full max-w-6xl content-center items-center gap-10 px-5 py-10 lg:grid-cols-[1fr_auto] lg:gap-16 lg:px-10">
        {/* The 3D mark, the name and what the product does; left-aligned beside the form on wide screens. */}
        <section className="flex flex-col items-center text-center text-white lg:items-start lg:text-left">
          <Logo3D className="size-28 lg:-ml-6 lg:size-48" />
          <h1 className="mt-2 text-3xl font-semibold tracking-tight lg:mt-4 lg:text-5xl">
            Agent Platform
          </h1>
          <p className="mt-3 max-w-[44ch] text-base leading-relaxed text-pretty text-white/65 lg:mt-4 lg:text-lg">
            Build AI agents, give them tools and workflows, and publish each one as a secure API
            endpoint.
          </p>
          <ul className="mt-8 hidden gap-3.5 text-sm text-white/75 lg:grid">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04]">
                  <Icon className="size-4 text-violet-300" aria-hidden />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </section>
        {/* Apple-style glass: a translucent, blurred and saturated pane with a specular top edge. */}
        <Card
          data-testid="login-card"
          className="relative mx-auto w-full max-w-sm rounded-3xl lg:w-sm border border-white/10 bg-black/55 text-white shadow-[0_24px_80px_-12px_rgb(0_0_0/0.7),inset_0_1px_0_rgb(255_255_255/0.12)] backdrop-blur-2xl backdrop-saturate-150 [--card-spacing:--spacing(6)] before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit] before:bg-gradient-to-b before:from-white/[0.07] before:via-white/[0.015] before:to-transparent"
        >
          <CardHeader className="relative">
            <CardTitle className="text-xl font-semibold tracking-tight">Sign in</CardTitle>
            <CardDescription className="text-white/60">
              Use your work account to continue.
            </CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <form onSubmit={submit} className="grid gap-4" noValidate>
              <ErrorAlert error={login.error ?? (error ? new Error(error) : null)} />
              <div className="grid gap-2">
                <Label htmlFor="email" className="text-white/80">
                  Email
                </Label>
                <Input
                  className={glassInput}
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password" className="text-white/80">
                  Password
                </Label>
                <Input
                  className={glassInput}
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                className="mt-1 h-10 rounded-full bg-white text-[#0b0b0f] hover:bg-white/90 disabled:bg-white/60"
                disabled={login.isPending || !email || !password}
              >
                {login.isPending ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
            {providers.data?.google && (
              <>
                <div className="my-4 flex items-center gap-3 text-xs text-white/40" aria-hidden>
                  <span className="h-px flex-1 bg-white/10" />
                  or
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <Button
                  asChild
                  variant="outline"
                  className="h-10 w-full rounded-full border-white/15 bg-white/[0.06] text-white hover:bg-white/10 hover:text-white"
                >
                  <a href={googleUrl}>
                    <GoogleMark />
                    Continue with Google
                  </a>
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
